import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Alert, 
  AlertTitle, 
  CircularProgress,
  Button
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import FormRenderer from './FormRenderer';
import formService from '../../../services/formService';
import { api } from '../../../services/api';
import trackingService from '../../../services/trackingService';

/**
 * Component that handles form rendering and submission
 * Works with both authenticated and public forms
 */
const FormSubmissionHandler = ({ 
  formId, 
  shareToken, 
  onSubmitSuccess, 
  onSubmitError,
  readOnly = false,
  initialValues = {},
  redirectPath,
  setSubmitting, // External state setter for submission status
  onFormLoaded // Callback to pass form data to parent component
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(null);
  const [submissionId, setSubmissionId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Flag to track if we've already fetched the form
  const [formFetched, setFormFetched] = useState(false);

  // Fetch form data
  useEffect(() => {
    // OPTIMIZATION: Skip fetching if we already have the form
    if (formFetched && form) {
      return;
    }
    
    // IMPORTANT: Don't reset an existing submission ID when re-fetching the form
    // This prevents losing connection between submissions
    
    const fetchForm = async () => {
      try {
        setLoading(true);
        setError(null);
        
        let formData;
        let response;
        
        // Fetch form based on whether it's a shared form or direct access
        if (shareToken) {
          // Fetch public form using share token
          response = await formService.getSharedForm(shareToken);
          setFormFetched(true); // Mark as fetched to prevent unnecessary requests
          
          // Extract form from the response - handles different API response structures
          
          if (response.data?.formDefinition) {
            // Exact structure from server response example
            formData = response.data.formDefinition;
          } else if (response.form) {
            // Form directly in response.form
            formData = response.form;
          } else if (response.formDefinition) {
            // Form directly in response.formDefinition
            formData = response.formDefinition;
          } else if (response.formDefinitions?.length > 0) {
            // Form in formDefinitions array
            formData = response.formDefinitions[0];
          } else {
            // Fallback to entire response
            formData = response;
          }
                    
          // Add UI-specific properties if they don't exist
          formData = {
            ...formData,
            isActive: formData.status === 'active',
            isPublished: formData.status !== 'draft'
          };
          
          // Check if form requires password
          if (formData.access?.passwordProtected && !formData.access?.authenticated) {
            // If location state doesn't have password, redirect to password entry page
            if (!location.state?.password) {
              navigate(`/forms/shared/${shareToken}/password`, { 
                state: { 
                  returnPath: location.pathname
                }
              });
              return;
            }
          }
        } else if (formId) {
          // Fetch form directly using form ID (authenticated access)
          response = await formService.getFormById(formId);
          setFormFetched(true); // Mark as fetched to prevent unnecessary requests
          
          // Extract form from the response - handles different API response structures
          
          if (response.data?.formDefinition) {
            // Exact structure from server response example
            formData = response.data.formDefinition;
          } else if (response.form) {
            // Form directly in response.form
            formData = response.form;
          } else if (response.formDefinition) {
            // Form directly in response.formDefinition
            formData = response.formDefinition;
          } else if (response.formDefinitions?.length > 0) {
            // Form in formDefinitions array
            formData = response.formDefinitions[0];
          } else {
            // Fallback to entire response
            formData = response;
          }
                    
          // Add UI-specific properties if they don't exist
          formData = {
            ...formData,
            isActive: formData.status === 'active',
            isPublished: formData.status !== 'draft'
          };
        } else {
          throw new Error('Either formId or shareToken must be provided');
        }
        
        // Validate form data before setting state
        if (!formData) {
          throw new Error('Unable to extract form data from server response');
        }
        
        // Set form state first
        setForm(formData);
        
        // Notify parent component of form data
        if (onFormLoaded && typeof onFormLoaded === 'function') {
          onFormLoaded(formData);
        }
        
        // OPTIMIZATION: Don't initialize submission when the form loads
        // Instead, wait until the form is submitted to initialize
        // This reduces unnecessary API calls and improves performance
      } catch (err) {
        setError(err.message || 'Failed to load form');
      } finally {
        setLoading(false);
      }
    };
    
    fetchForm();
  }, [formId, shareToken, location, navigate, readOnly]);

  // Initialize form submission to get submission ID for file uploads
  const initializeSubmission = async (formId, shareToken) => {
    try {
      // Check if we have either a form ID or share token
      if (!formId && !shareToken) {
        // If we're in development, generate a fake submission ID instead of failing
        if (process.env.NODE_ENV === 'development') {
          const fakeId = `dev-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
          return { 
            _id: fakeId, 
            id: fakeId, 
            status: 'draft' 
          };
        }
        
        throw new Error('Either formId or shareToken must be provided for initialization');
      }
      
      // Create initial submission record with metadata
      const metadata = {
        userAgent: navigator.userAgent,
        referrer: document.referrer,
        language: navigator.language,
        timestamp: new Date().toISOString(),
        initializeAttempt: true // Flag to indicate this is an initialization request
      };
      
      // Initializing submission for shared or authenticated form
      
      // Create dummy data to satisfy server-side data requirement
      // We need to ensure we have at least a non-empty data object
      const dummyData = {
        _initTimestamp: new Date().toISOString(),
        _initialized: true
      };
      
      let response;
      
      if (shareToken) {
        // Initialize submission for public form
        const initData = {
          data: dummyData, // Use dummy data to satisfy the requirement
          status: 'draft',
          action: 'initialize',
          metadata
        };
        
        // Sending shared form initialization request
        
        // If password protected, include password from location state
        if (location.state?.password) {
          response = await formService.submitSharedForm(shareToken, {
            ...initData, 
            password: location.state.password
          });
        } else {
          response = await formService.submitSharedForm(shareToken, initData);
        }
      } else {
        // Initialize submission for authenticated form
        response = await formService.submitForm(formId, {
          data: dummyData, // Use dummy data to satisfy the requirement
          status: 'draft',
          metadata,
          action: 'initialize',
          formDefinition: formId // Use formDefinition instead of formId to match API changes
        });
      }
      
      // Validate response
      
      if (!response) {
        throw new Error('No response received from server during initialization');
      }
      
      if (!response.submission) {
        throw new Error('Server response missing submission data');
      }
      
      if (!response.submission._id && !response.submission.id) {
        throw new Error('Server returned submission without ID');
      }
      
      // Extract ID, handling different response formats with more thorough parsing
      // This is a critical section - we need to be very careful about extracting the ID
      
      // Try multiple paths to find the ID
      let submissionId = null;
      
      // Direct path in submission object
      if (response.submission && (response.submission._id || response.submission.id)) {
        submissionId = response.submission._id || response.submission.id;
      }
      // Check if response itself is the submission
      else if (response._id || response.id) {
        submissionId = response._id || response.id;
      }
      // Look in data property
      else if (response.data && response.data.submission && (response.data.submission._id || response.data.submission.id)) {
        submissionId = response.data.submission._id || response.data.submission.id;
      }
      // Check for _id or id directly in the submission object if it's not in the expected properties
      else if (response.submission) {
        // Try to find any property that might be an ID
        const possibleIdProps = Object.keys(response.submission).filter(key => 
          key.toLowerCase().includes('id') || key === '_id');
        
        for (const prop of possibleIdProps) {
          if (response.submission[prop] && typeof response.submission[prop] === 'string') {
            submissionId = response.submission[prop];
            break;
          }
        }
      }
      
      // In development environment, generate a fake ID if we still can't find one
      if (!submissionId && process.env.NODE_ENV === 'development') {
        submissionId = `dev-temp-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
      }
      
      // If we still don't have an ID, this is a real error
      if (!submissionId) {
        throw new Error('Server returned submission without ID');
      }
      
      return {
        _id: submissionId,
        id: submissionId,
        status: response.submission?.status || 'draft'
      };
    } catch (err) {
      throw err;
    }
  };

  // Handle form submission
  const handleSubmit = async (formData) => {
    try {
      // Validate form data structure
      if (!formData || typeof formData !== 'object') {
        throw new Error('Invalid form data structure: Data must be an object');
      }
      
      // Set submitting state
      if (setSubmitting) {
        setSubmitting(true);
      }
      
      // Track form start event
      if (form && shareToken) {
        trackingService.trackFormStart(shareToken, form.title, {
          form_type: 'public',
          share_token: shareToken
        });
      }
      
      // OPTIMIZATION: Completely skip initialization phase since we now upload files during submission
      
      // Generate a temporary ID for the client-side only, to track file uploads
      // This isn't used by the server, as we'll do submission in one step now
      const tempClientId = `temp-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
      
      // Prepare submission data - ensure proper structure
      const submissionData = {
        data: formData || {}, // Ensure data field is explicitly provided and defaulted if null
        status: 'submitted',
        skipInitialization: true, // Signal to the server we're using the one-step approach
        submittedAt: new Date().toISOString(),
        formDefinition: form._id // Add formDefinition to match API changes
      };
      
      // Extra validation for data structure
      if (typeof submissionData.data !== 'object' || Array.isArray(submissionData.data)) {
        // Try to parse if it's a string that might be JSON
        if (typeof submissionData.data === 'string') {
          try {
            submissionData.data = JSON.parse(submissionData.data);
          } catch (e) {
            // If it can't be parsed as JSON, create an empty object with the string as a value
            submissionData.data = { value: submissionData.data };
          }
        } else {
          // For other non-object types, create a wrapper object
          submissionData.data = { value: submissionData.data };
        }
      }
      
      // Add password if password-protected form
      if (location.state?.password) {
        submissionData.password = location.state.password;
      }
      
      // Submit form data - check if we have files to determine submission method
      let response;
      
      // Check if form data contains files
      const hasFiles = Object.values(formData).some(value => 
        value instanceof File || 
        (value && typeof value === 'object' && value.file) ||
        (Array.isArray(value) && value.some(item => item instanceof File || (item && typeof item === 'object' && item.file)))
      );
      
      // Prepare data without files for tracking
      const dataWithoutFiles = {};
      Object.entries(formData).forEach(([key, value]) => {
        if (!(value instanceof File) && 
            (!Array.isArray(value) || !value.some(item => item instanceof File)) &&
            !(value && typeof value === 'object' && value.file)) {
          dataWithoutFiles[key] = value;
        }
      });
      
      // Check if form has files
      
      try {
        if (hasFiles) {
          // When we have files, we need to submit using FormData so the backend middleware can process them
          
          // Create FormData object
          const formDataToSubmit = new FormData();
          
          // Add form data as a JSON string
          formDataToSubmit.append('data', JSON.stringify(dataWithoutFiles));
          
          // Add other required fields
          formDataToSubmit.append('formId', submissionData.formId);
          formDataToSubmit.append('formVersion', submissionData.formVersion);
          if (submissionData.language) {
            formDataToSubmit.append('language', submissionData.language);
          }
          if (submissionData.metadata) {
            formDataToSubmit.append('metadata', JSON.stringify(submissionData.metadata));
          }
          
          // Add files to FormData
          Object.entries(formData).forEach(([fieldName, fieldValue]) => {
            if (fieldValue instanceof File) {
              formDataToSubmit.append(fieldName, fieldValue);
            } else if (Array.isArray(fieldValue)) {
              fieldValue.forEach(item => {
                if (item instanceof File) {
                  formDataToSubmit.append(fieldName, item);
                } else if (item && item.file instanceof File) {
                  formDataToSubmit.append(fieldName, item.file);
                }
              });
            } else if (fieldValue && fieldValue.file instanceof File) {
              formDataToSubmit.append(fieldName, fieldValue.file);
            }
          });
          
          // Submit using FormData - this will be handled by backend's handleS3Upload middleware
          if (shareToken) {
            const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002';
            response = await fetch(`${API_BASE_URL}/forms/public/${shareToken}/submit`, {
              method: 'POST',
              body: formDataToSubmit
              // Don't set Content-Type header - let browser set it with boundary
            });
            
            if (!response.ok) {
              throw new Error(`Submission failed: ${response.status} ${response.statusText}`);
            }
            
            response = await response.json();
          } else {
            // For authenticated form submission with files, we need to use the API service differently
            response = await api.post(`/forms/submissions`, formDataToSubmit, {
              headers: {
                // Let axios handle the content-type for FormData
              }
            });
          }
        } else {
          // No files - submit as regular JSON
          
          if (shareToken) {
            response = await formService.submitSharedForm(shareToken, submissionData);
          } else {
            submissionData.formDefinition = form._id;
            response = await formService.submitForm(form._id, submissionData);
          }
        }
        
        // Form submission successful
        
        // Validate the response
        if (!response) {
          throw new Error('No response received from server');
        }
        
        // Handle different response structures
        const submissionObject = response.submission || response.data?.submission;
        
        if (!submissionObject) {
          throw new Error('Server response missing submission data');
        }
        
        // Normalize the submission object to handle different response formats
        const submissionResult = {
          _id: submissionObject._id || submissionObject.id,
          id: submissionObject._id || submissionObject.id,
          status: submissionObject.status || 'submitted',
          form: form || submissionObject.form || { title: 'Form Submission' }
        };
        
        // Track successful submission
        if (form && shareToken) {
          trackingService.trackFormSubmission(
            shareToken, 
            form.title,
            submissionResult._id,
            dataWithoutFiles, // Track form data without files
            {
              form_type: 'public',
              share_token: shareToken,
              has_files: hasFiles
            }
          );
        }
        
        // Reset submitting state on success - ensure this always happens
        if (setSubmitting && typeof setSubmitting === 'function') {
          setSubmitting(false);
        }
        
        // Call success callback if provided - this should handle the redirection
        // This is a critical section - ALWAYS call the success callback if provided
        if (onSubmitSuccess && typeof onSubmitSuccess === 'function') {
          try {
            onSubmitSuccess(submissionResult);
          } catch (callbackError) {
            // Continue execution even if the callback fails
          }
        } else {
          
          // Only use redirectPath as a fallback if onSubmitSuccess is not provided
          if (redirectPath) {
            navigate(redirectPath, { 
              state: { 
                submission: submissionResult,
                formTitle: form.title,
                submissionSuccess: true
              }
            });
          }
        }
        
        return submissionResult;
      } catch (submitError) {
        
        // Track submission error
        if (form && shareToken) {
          trackingService.trackFormError(
            shareToken,
            form.title,
            submitError.message || 'Unknown error',
            {
              form_type: 'public',
              share_token: shareToken,
              error_type: 'submission_error'
            }
          );
        }
        
        // Reset submitting state on error - ensure this always happens
        if (setSubmitting && typeof setSubmitting === 'function') {
          setSubmitting(false);
        }
        
        // Set more descriptive error
        const errorMessage = `Form submission failed: ${submitError.message || 'Unknown error'}`;
        setError(errorMessage);
        
        // Call onSubmitError callback if provided
        if (onSubmitError && typeof onSubmitError === 'function') {
          try {
            onSubmitError(submitError);
          } catch (callbackError) {
            // Continue execution even if the callback fails
          }
        }
        
        // Don't throw the error - this prevents the form from being unusable
        // Just return null to indicate failure
        return null;
      }
    } catch (err) {
      
      // Reset submitting state on error
      if (setSubmitting) {
        setSubmitting(false);
      }
      
      // Show error to user
      setError(err.message || 'Failed to submit form. Please try again.');
      
      // Call error callback if provided
      if (onSubmitError && typeof onSubmitError === 'function') {
        try {
          onSubmitError(err);
        } catch (callbackError) {
          // Continue execution even if the callback fails
        }
      }
      
      throw err;
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Error state
  if (error) {
    return (
      <Box>
        <Alert 
          severity="error" 
          sx={{ 
            my: 2,
            '& .MuiAlert-icon': {
              fontSize: '1.5rem'
            }
          }}
        >
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={() => {
              setError(null);
              setSubmissionId(null);
              // Reload the current page to restart the form process
              window.location.reload();
            }}
            sx={{ mr: 2 }}
          >
            Reload Form
          </Button>
          
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/')}
          >
            Return to Home
          </Button>
        </Box>
      </Box>
    );
  }
  
  // No form found
  if (!form) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Form not found
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          Return to Home
        </Button>
      </Paper>
    );
  }
  
  // Check if form is active
  if (!form.isActive && !readOnly) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          This form is no longer accepting submissions
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          Return to Home
        </Button>
      </Paper>
    );
  }

  return (
    <FormRenderer
      formDefinition={form}
      rawForm={form} // Pass the raw form as well for debugging
      rawResponse={form} // Pass the entire response for debugging
      onSubmit={handleSubmit}
      initialValues={initialValues}
      readOnly={readOnly}
      // With one-step submission, we don't need a predetermined submission ID
      // Client-side IDs will be generated during submission
      shareToken={shareToken} // Pass the share token for public forms
    />
  );
};

export default FormSubmissionHandler;